/**
 * @file Today.tsx
 * @description "Dziś" — the daily life-layer screen (GET /api/today). Four
 * sections: Top 3 of the day (starred tasks), slipping tasks (7+ days without
 * activity, hidden when empty), routines with today-checkbox + streak flame,
 * and the remaining open tasks. Data is polled every 30 seconds like Inbox.tsx.
 * All page copy is intentionally Polish (see the today/tasks i18n namespaces) —
 * same convention as Inbox.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarCheck,
  Check,
  Clock,
  Flame,
  ListTodo,
  RefreshCw,
  Star,
  TrendingDown,
  Repeat,
} from "lucide-react";
import { api } from "../lib/api";
import type { LifeRoutineEnriched, TodayPayload } from "../lib/api";
import { domainMap } from "../lib/life";
import { LifeTaskRow } from "../components/LifeTaskRow";
import { EmptyState } from "../components/EmptyState";
import { timeAgo } from "../lib/format";
import type { LucideIcon } from "lucide-react";

const POLL_INTERVAL_MS = 30_000;

function SectionHeader({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className="w-6 h-6 rounded-md bg-accent/15 border border-accent/25 flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-accent" aria-hidden />
      </span>
      <h2 className="text-sm font-semibold text-gray-100">{title}</h2>
    </div>
  );
}

function RoutineRow({
  routine,
  busy,
  onToggle,
}: {
  routine: LifeRoutineEnriched;
  busy: boolean;
  onToggle: (id: string) => void;
}) {
  const { t } = useTranslation("routines");
  return (
    <div className="card-hover p-3 flex items-center gap-3">
      <button
        onClick={() => onToggle(routine.id)}
        disabled={busy}
        title={t("checkToday")}
        aria-label={t("checkToday")}
        className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-60 ${
          routine.checkedToday
            ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-400"
            : "border-border bg-surface-2 text-transparent hover:border-emerald-500/40"
        }`}
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-200 truncate">{routine.name}</p>
        <p className="text-[11px] text-gray-400">
          {t(`timeOfDayNames.${routine.time_of_day}`)}
          {routine.specific_time ? ` · ${routine.specific_time}` : ""}
        </p>
      </div>
      {routine.streak > 0 && (
        <span className="badge bg-orange-500/10 text-orange-400 border-orange-500/20 inline-flex items-center gap-1 flex-shrink-0">
          <Flame className="w-3 h-3" aria-hidden />
          {routine.streak}
        </span>
      )}
    </div>
  );
}

export function Today() {
  const { t } = useTranslation("today");
  const [searchParams] = useSearchParams();
  const rawScope = searchParams.get("scope");
  const scope = rawScope === "praca" || rawScope === "prywatne" ? rawScope : undefined;
  const [data, setData] = useState<TodayPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const load = useCallback(async () => {
    try {
      setData(await api.today.get(scope));
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const withBusy = useCallback(
    async (id: string, action: () => Promise<unknown>) => {
      setBusyId(id);
      setActionError(null);
      try {
        await action();
        await load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const onToggleDone = useCallback(
    (id: string) => withBusy(id, () => api.tasks.toggleDone(id)),
    [withBusy]
  );
  const onToggleTop3 = useCallback(
    (id: string) => withBusy(id, () => api.tasks.toggleTop3(id)),
    [withBusy]
  );
  const onToggleRoutine = useCallback(
    (id: string) => withBusy(id, () => api.routines.toggleCheck(id)),
    [withBusy]
  );

  const domains = useMemo(() => domainMap(data?.domains ?? []), [data?.domains]);

  return (
    <div className="animate-fade-in space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
            <CalendarCheck className="w-4.5 h-4.5 text-accent" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-100">{t("title")}</h1>
            <p className="text-xs text-gray-500 flex items-center gap-2">
              {t("subtitle")}
              <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 bg-surface-2 border border-border px-2 py-0.5 rounded-md font-mono ml-2">
                <Clock className="w-3 h-3" />
                {lastUpdate.toLocaleTimeString()}
              </span>
            </p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost" disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          {t("common:refresh")}
        </button>
      </div>

      {/* Action error banner */}
      {actionError && (
        <div className="card p-3 flex items-center gap-3 border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-gray-300 min-w-0 truncate">
            {t("actionError")}: <span className="font-mono text-gray-500">{actionError}</span>
          </p>
        </div>
      )}

      {/* Error state (endpoint unreachable) */}
      {error && !data && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-gray-200">{t("loadError")}</p>
            <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && !error && (
        <div className="space-y-3 max-w-3xl">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse h-20 bg-surface-2 rounded-xl" />
          ))}
        </div>
      )}

      {data && (
        <div className="max-w-3xl space-y-8">
          {/* Top 3 of the day */}
          <section>
            <SectionHeader icon={Star} title={t("top3Title")} />
            {data.top3.length > 0 ? (
              <div className="space-y-2">
                {data.top3.map((task) => (
                  <LifeTaskRow
                    key={task.id}
                    task={task}
                    domains={domains}
                    busy={busyId === task.id}
                    onToggleDone={onToggleDone}
                    onToggleTop3={onToggleTop3}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic px-1">{t("top3Empty")}</p>
            )}
          </section>

          {/* Slipping — only when non-empty */}
          {data.slipping.length > 0 && (
            <section>
              <SectionHeader icon={TrendingDown} title={t("slippingTitle")} />
              <div className="space-y-2">
                {data.slipping.map((task) => (
                  <LifeTaskRow
                    key={task.id}
                    task={task}
                    domains={domains}
                    busy={busyId === task.id}
                    onToggleDone={onToggleDone}
                    onToggleTop3={onToggleTop3}
                    meta={timeAgo(task.last_activity_at)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Routines */}
          <section>
            <SectionHeader icon={Repeat} title={t("routinesTitle")} />
            {data.routines.length > 0 ? (
              <div className="space-y-2">
                {data.routines.map((routine) => (
                  <RoutineRow
                    key={routine.id}
                    routine={routine}
                    busy={busyId === routine.id}
                    onToggle={onToggleRoutine}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic px-1">{t("routinesEmpty")}</p>
            )}
          </section>

          {/* Remaining open tasks */}
          <section>
            <SectionHeader icon={ListTodo} title={t("openTitle")} />
            {data.openTasks.length > 0 ? (
              <div className="space-y-2">
                {data.openTasks.map((task) => (
                  <LifeTaskRow
                    key={task.id}
                    task={task}
                    domains={domains}
                    busy={busyId === task.id}
                    onToggleDone={onToggleDone}
                    onToggleTop3={onToggleTop3}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic px-1">{t("openEmpty")}</p>
            )}
          </section>

          {/* Whole-day empty state */}
          {data.top3.length === 0 &&
            data.slipping.length === 0 &&
            data.routines.length === 0 &&
            data.openTasks.length === 0 && (
              <EmptyState
                icon={CalendarCheck}
                title={t("emptyTitle")}
                description={t("emptyDescription")}
              />
            )}
        </div>
      )}
    </div>
  );
}
